import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { CheckCircle2, XCircle, Clock, Loader2, Send, Link2 } from "lucide-react";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "", label: "All" },
];

export default function AdminFeedbackVideos() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  const [requestEmail, setRequestEmail] = useState("");
  const [requestMessage, setRequestMessage] = useState("");
  const [sendingRequest, setSendingRequest] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/feedback-videos", { params: tab ? { status: tab } : {} })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [tab]);

  const loadRequests = () => {
    setLoadingRequests(true);
    api.get("/admin/feedback-video-requests").then((r) => setRequests(r.data)).finally(() => setLoadingRequests(false));
  };
  useEffect(() => { loadRequests(); }, []);

  const sendRequest = async () => {
    const email = requestEmail.trim();
    if (!email) { toast.error("Enter the user's email"); return; }
    setSendingRequest(true);
    try {
      await api.post("/admin/feedback-videos/request", { email, message: requestMessage.trim() || null });
      toast.success(`Upload link emailed to ${email}`);
      setRequestEmail("");
      setRequestMessage("");
      loadRequests();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send request");
    } finally {
      setSendingRequest(false);
    }
  };

  const approve = async (video) => {
    setActingId(video.id);
    try {
      await api.post(`/admin/feedback-videos/${video.id}/approve`);
      toast.success("Approved — publishing to Instagram/YouTube");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to approve");
    } finally { setActingId(null); }
  };

  const copyLink = async (video) => {
    try {
      await navigator.clipboard.writeText(video.video_url);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy — copy it from the video player instead");
    }
  };

  const reject = async (video) => {
    const reason = window.prompt("Reason for rejecting this video? (optional)") || "";
    setActingId(video.id);
    try {
      await api.post(`/admin/feedback-videos/${video.id}/reject`, { reason: reason || null });
      toast.success("Rejected");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to reject");
    } finally { setActingId(null); }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <span className="label-mono">Admin</span>
        <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter mt-2">Feedback Videos</h1>
        <p className="text-[#52525B] mt-2">Review student testimonials before they publish to Instagram Reels and YouTube.</p>

        <div className="mt-8 card-brutal p-6 bg-white">
          <span className="label-mono">Request a feedback video</span>
          <p className="text-sm text-[#52525B] mt-1">Email a user a link to upload their feedback video.</p>
          <div className="mt-4 flex flex-col md:flex-row gap-3">
            <input
              type="email"
              value={requestEmail}
              onChange={(e) => setRequestEmail(e.target.value)}
              placeholder="user@example.com"
              data-testid="feedback-video-request-email"
              className="flex-1 px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="text"
              value={requestMessage}
              onChange={(e) => setRequestMessage(e.target.value)}
              placeholder="Optional personal note"
              data-testid="feedback-video-request-message"
              className="flex-1 px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={sendRequest}
              disabled={sendingRequest}
              data-testid="feedback-video-request-submit"
              className="btn-brutal bg-blue-600 text-white px-5 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-50 whitespace-nowrap"
            >
              {sendingRequest ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} strokeWidth={2.5} />}
              Send link
            </button>
          </div>

          {!loadingRequests && requests.length > 0 && (
            <div className="mt-5 space-y-2">
              {requests.slice(0, 8).map((r) => (
                <div key={r.id} data-testid={`feedback-video-request-${r.id}`} className="text-xs text-[#52525B] flex items-center justify-between gap-2 border-t border-dashed border-[#0A0A0A]/20 pt-2">
                  <span>{r.user_name} &lt;{r.user_email}&gt;</span>
                  <span>{r.created_at ? format(new Date(r.created_at), "dd MMM yyyy, h:mm a") : "—"} · {r.email_status === "sent" ? "Sent" : r.email_status === "not_configured" ? "Email not configured" : "Failed"}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.key || "all"}
              onClick={() => setTab(t.key)}
              data-testid={`feedback-video-tab-${t.key || "all"}`}
              className={`btn-brutal px-4 py-2 text-sm ${tab === t.key ? "bg-blue-600 text-white" : "bg-white"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <div className="label-mono">Loading…</div>
          ) : items.length === 0 ? (
            <div data-testid="no-feedback-videos" className="text-[#52525B]">No videos in this category.</div>
          ) : (
            items.map((v) => (
              <div key={v.id} data-testid={`feedback-video-${v.id}`} className="card-brutal p-5 bg-white">
                <video src={v.video_url} controls className="w-full rounded-xl border-2 border-[#0A0A0A] bg-black aspect-[9/16] max-h-96 object-contain" />
                <div className="mt-3 flex items-center justify-between gap-2">
                  <div>
                    <div className="font-semibold">{v.user_name}</div>
                    <div className="text-xs text-[#52525B]">{v.user_email}</div>
                  </div>
                  <span className="text-xs text-[#52525B]">{v.duration_seconds?.toFixed(1)}s</span>
                </div>
                {v.caption && <p className="text-sm mt-2 text-[#0A0A0A]/80 leading-relaxed">"{v.caption}"</p>}
                <div className="mt-3 text-xs text-[#52525B]">
                  Submitted {v.created_at ? format(new Date(v.created_at), "dd MMM yyyy, h:mm a") : "—"}
                </div>

                {v.status === "pending" ? (
                  <div className="mt-4 flex gap-2">
                    <button
                      onClick={() => approve(v)}
                      disabled={actingId === v.id}
                      data-testid={`approve-video-${v.id}`}
                      className="btn-brutal bg-[#A7F3D0] px-3 py-2 text-xs flex items-center gap-1.5 flex-1 justify-center disabled:opacity-60"
                    >
                      {actingId === v.id ? <Loader2 className="animate-spin" size={12} /> : <CheckCircle2 size={12} strokeWidth={2.5} />} Approve
                    </button>
                    <button
                      onClick={() => reject(v)}
                      disabled={actingId === v.id}
                      data-testid={`reject-video-${v.id}`}
                      className="btn-brutal bg-[#FFDDBF] px-3 py-2 text-xs flex items-center gap-1.5 flex-1 justify-center disabled:opacity-60"
                    >
                      <XCircle size={12} strokeWidth={2.5} /> Reject
                    </button>
                  </div>
                ) : v.status === "approved" ? (
                  <div className="mt-4 space-y-1.5 text-xs">
                    <button
                      onClick={() => copyLink(v)}
                      data-testid={`copy-video-link-${v.id}`}
                      className="btn-brutal bg-white px-3 py-2 text-xs w-full flex items-center justify-center gap-1.5 mt-2"
                    >
                      <Link2 size={12} strokeWidth={2.5} /> Copy link
                    </button>
                    <div className="text-[10px] text-[#52525B] break-all">{v.video_url}</div>
                    <div className="text-[10px] text-green-700">Shown on the home page testimonials.</div>
                  </div>
                ) : (
                  <div className="mt-4 flex items-center gap-1.5 text-xs text-red-700">
                    <Clock size={12} strokeWidth={2.5} /> Rejected{v.reject_reason ? `: ${v.reject_reason}` : ""}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
